import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Hooks for the public dataset API (`GET /api/v1/data/:apiKey`).
 *
 * These datasets are published from scraper-backed pipelines and served
 * read-only by API key (see services/api-service public-data.routes.ts and
 * docs/*-api.md). The endpoint paginates (max 500/page) and supports JSONB
 * equality filters as query params.
 */

export interface PublicDatasetResponse<T = Record<string, unknown>> {
  datasetId: string;
  tableName: string;
  totalRecords: number;
  unfilteredTotal: number;
  filters?: Record<string, string>;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
  schema: Array<{ name: string; type: string }>;
  data: T[];
}

export interface PublicDatasetAll<T = Record<string, unknown>> {
  rows: T[];
  tableName: string;
  totalRecords: number;
}

/**
 * Fetch an ENTIRE public dataset (all pages) for client-side filtering/pivoting.
 * Suitable for the small reference datasets (a few thousand rows at most).
 */
export function usePublicDatasetAll<T = Record<string, unknown>>(
  apiKey: string,
  options?: { pageSize?: number; enabled?: boolean }
) {
  const pageSize = options?.pageSize ?? 500;
  return useQuery({
    queryKey: ['public-dataset', apiKey, 'all', pageSize],
    enabled: options?.enabled ?? Boolean(apiKey),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PublicDatasetAll<T>> => {
      const rows: T[] = [];
      let page = 1;
      let tableName = '';
      let totalRecords = 0;
      // hard cap on pages as a runaway guard
      for (let guard = 0; guard < 50; guard++) {
        const res = await apiClient.get<PublicDatasetResponse<T>>(
          `/data/${apiKey}?page=${page}&limit=${pageSize}`
        );
        rows.push(...res.data);
        tableName = res.tableName;
        totalRecords = res.unfilteredTotal ?? res.totalRecords;
        if (!res.hasMore) break;
        page += 1;
      }
      return { rows, tableName, totalRecords };
    },
  });
}

/** Direct (browser) URL to the Excel export of a public dataset. */
export function publicDatasetExportUrl(apiKey: string): string {
  const base = import.meta.env.VITE_API_URL || '/api/v1';
  return `${base}/data/${apiKey}/export`;
}
